import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UserRoleUid } from '@constants';
import { DesktopDirective, MobileDirective, TabletLandscapeDirective, TabletPortraitDirective } from '@directives';
import { AuthService, KeplerSfdcService, UserService } from '@services';
import { KeplerDBUserProfile } from '@shared/services/kepler-db/kepler-db.interface';
import { KeplerDbService } from '@shared/services/kepler-db/kepler-db.service';
import { getInitialsColors } from '@shared/utils/initials-color';
import { scrollToTopScrolling } from '@utils';
import dayjs from 'dayjs';
import { BehaviorSubject, Subscription, combineLatest, firstValueFrom, map, startWith } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { ProductService } from '../@shared/services/product';
import { GknRoutePath } from '../app-routing.enum';
import { AppHeaderDesktopComponent } from './app-header-desktop/app-header-desktop.component';
import { AppHeaderMobileComponent } from './app-header-mobile/app-header-mobile.component';
import { AppHeaderSearchBarComponent } from './app-header-search-bar/app-header-search-bar.component';
import { AppHeaderSharedService } from './app-header-shared.service';

@Component({
    selector: 'app-header',
    imports: [
        CommonModule,
        AppHeaderDesktopComponent,
        AppHeaderMobileComponent,
        DesktopDirective,
        MobileDirective,
        TabletLandscapeDirective,
        TabletPortraitDirective,
    ],
    templateUrl: './app-header.component.html',
})
export class AppHeaderComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);
    headerShared = inject(AppHeaderSharedService);
    private productService = inject(ProductService);
    private router = inject(Router);
    private salesforce = inject(KeplerSfdcService);
    private user = inject(UserService);
    private dbService = inject(KeplerDbService);

    private subs: Subscription[] = [];

    activePage: string = '';

    // Shared observables
    selectedProduct$ = this.productService.selectedProduct$;
    initials$ = this.user.initials$;
    initialsColors$ = this.user.initials$.pipe(map((initials) => getInitialsColors(initials)));
    pfpLink$ = this.user.pfpLink$;
    toggleSearchBar$ = new BehaviorSubject(false);

    portalAccess$ = this.user.userRole$.pipe(
        map((userRole) => ({
            showConsultantPortal: userRole === UserRoleUid.CONSULTANT,
            showPartnerPortal: userRole === UserRoleUid.PARTNER,
        })),
    );

    // NOTE: The Resources Mega Menu observable is used for both mobile and desktop menus
    resourcesMegaMenu$ = this.selectedProduct$.pipe(map((product) => this.headerShared.getResourcesMenu(product?.uid)));
    supportMegaMenu$ = combineLatest([
        this.selectedProduct$,
        this.salesforce.permissionsRecord$.pipe(startWith(null)),
    ]).pipe(map(([product, permissions]) => this.headerShared.getSupportMenu(product?.uid, permissions?.MySupport)));

    @ViewChild(AppHeaderSearchBarComponent) searchBar: AppHeaderSearchBarComponent;

    ngOnInit(): void {
        this.subs.push(
            this.user.email$
                .pipe(
                    filter((email) => email.length > 0),
                    switchMap((email) => {
                        const newData: KeplerDBUserProfile = { email: email, lastAccessDate: dayjs().toISOString() };
                        return this.dbService.updateProfileData(email, newData);
                    }),
                )
                .subscribe(),
        );

        this.subs.push(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe((event: NavigationEnd) => {
                    this.activePage = event.urlAfterRedirects;
                    if (this.activePage.includes('?')) {
                        this.activePage = this.activePage.split('?')[0];
                    }
                }),
        );
    }

    /**
     * Sing in user
     * @param e - Event
     */
    signIn(e?: Event): void {
        e?.stopPropagation();
        this.authService.signInWithRedirect();
    }

    /**
     * Sign out user
     * @param e - Event
     */
    signOut(e?: Event): void {
        e?.stopPropagation();
        this.authService
            .signOut()
            .then(() => firstValueFrom(this.selectedProduct$))
            .then((selectedProduct) => {
                this.router.navigate([`/${GknRoutePath.HOME}`], {
                    queryParams: { product: selectedProduct },
                    queryParamsHandling: 'merge',
                });
            })
            .catch((e) => {
                throw e;
            });
    }

    /**
     * Toggle to show or hide search bar
     * @param e - event
     */
    toggleSearchBar(e?: Event): void {
        e?.stopPropagation();
        this.toggleSearchBar$.next(!this.toggleSearchBar$.value);
        if (this.searchBar) {
            if (this.toggleSearchBar$.value) {
                this.searchBar.focus();
            } else {
                this.searchBar.blur();
            }
        }
    }

    /**
     * Scroll to header top
     */
    scrollToTop(): void {
        scrollToTopScrolling();
    }

    /**
     * Unsubscribe from all subscriptions on destroy
     * @returns void
     */
    ngOnDestroy(): void {
        this.subs?.forEach((sub) => sub?.unsubscribe());
    }
}
